import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { roleAllowed } from "@/lib/roles";
import { buildCustomerMap } from "@/lib/customerNames";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  FileCheck2, Plus, Download, Printer, XCircle, FileEdit, Trash2, CheckCircle2,
  UploadCloud, Receipt, Upload, FileSpreadsheet, QrCode, ShieldAlert, Files,
} from "lucide-react";
import { toast } from "sonner";
import { formatMoney, docCurrency, currencySymbol } from "@/lib/currency";

const STATUS = {
  draft: "bg-slate-100 text-slate-600",
  issued: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

// Invoice numbers are assigned by Tally; until it has numbered the Sales
// voucher the invoice only holds a TALLY-PENDING/ placeholder code.
const invoiceNo = (inv) => (inv?.tally_number_pending ? "Awaiting Tally no." : inv?.code);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const EMPTY_GENERATE_FORM = {
  dc_id: "", due_date: "", po_reference: "", po_date: "", eway_bill_no: "",
  destination: "", terms_of_delivery: "", reverse_charge: false, notes: "",
};

export default function TaxInvoicesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [list, setList] = useState([]);
  const [dcs, setDcs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerNames, setCustomerNames] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  // Sales vouchers pulled from Tally (Tally Data → Sales Invoices) — listed
  // read-only beside the ERP's own invoices.
  const [tallyList, setTallyList] = useState([]);
  const [selectedTally, setSelectedTally] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState(EMPTY_GENERATE_FORM);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    lines: [], due_date: "", po_reference: "", po_date: "", eway_bill_no: "",
    vehicle_no: "", transporter: "", destination: "", terms_of_delivery: "",
    reverse_charge: false, notes: "",
  });

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const [einvoiceCancelOpen, setEinvoiceCancelOpen] = useState(false);
  const [einvoiceCancelForm, setEinvoiceCancelForm] = useState({ reason_code: "4", remarks: "" });

  // Each flag matches its endpoint's require_role list exactly, including
  // role aliases (see roleAllowed) — e.g. "sales_crm" is aliased to "planner"
  // on the backend, so it must count as "planner" here too, or the button
  // stays hidden for a role the API would happily accept.
  const canEdit = roleAllowed(user?.role, ["admin", "planner", "operator"]);
  const canCancel = roleAllowed(user?.role, ["admin", "planner"]);
  const canDelete = roleAllowed(user?.role, ["admin", "planner"]);
  // GST e-Invoice (IRN) generation/cancellation — same role gate as Issue/Edit,
  // since it's a step in the same invoice-finalization workflow.
  const canEinvoice = canEdit;

  const custMap = useMemo(() => buildCustomerMap(customers, customerNames), [customers, customerNames]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const dcMap = useMemo(() => Object.fromEntries(dcs.map((d) => [d.id, d])), [dcs]);

  const eligibleDcs = useMemo(
    () => dcs.filter((d) => d.kind === "customer" && ["dispatched", "delivered"].includes(d.status) && !d.tax_invoice_id),
    [dcs]
  );

  const load = async () => {
    // No `kind` filter — every Delivery Challan (customer-outward AND
    // subcontract, every status) is fetched so the list below can reflect
    // all of them, not just the customer ones eligible for invoicing.
    // `eligibleDcs` below still narrows to the invoiceable subset for the
    // Generate-invoice dropdown/action.
    const [inv, dc, c, n, i] = await Promise.all([
      api.get("/tax-invoices"),
      api.get("/delivery-challans"),
      api.get("/customers"),
      api.get("/customers/names-lookup"),
      api.get("/items"),
    ]);
    setList(inv.data);
    setDcs(dc.data);
    setCustomers(c.data);
    setCustomerNames(n.data);
    setItems(i.data);
    if (selected) setSelected(inv.data.find((x) => x.id === selected.id) || null);
    // Separate call so a Tally hiccup never blocks the ERP's own invoices.
    try {
      const { data } = await api.get("/tally-sync/vouchers", { params: { kind: "sales_order", limit: 1000 } });
      setTallyList(data || []);
    } catch { setTallyList([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Deep-link support — "Generate Tax Invoice" / "View Invoice" from the
  // Delivery Challans page lands here with the invoice already selected.
  useEffect(() => {
    const id = location.state?.selectId;
    if (id && list.length) {
      const found = list.find((x) => x.id === id);
      if (found) setSelected(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectId, list.length]);

  const visibleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((inv) => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (inv.code || "").toLowerCase().includes(q) ||
        (inv.dc_code || "").toLowerCase().includes(q) ||
        (inv.customer_so_code || inv.so_code || "").toLowerCase().includes(q) ||
        (inv.po_reference || "").toLowerCase().includes(q) ||
        (inv.customer_name || "").toLowerCase().includes(q)
      );
    });
  }, [list, search, statusFilter]);

  // DCs already reflected via their own Tax Invoice row (below) — every
  // other DC gets a reference row of its own, so *every* Delivery Challan
  // (all 115, whatever kind/status) shows up somewhere in this list, not
  // just the ones already invoiced.
  const invoicedDcIds = useMemo(() => new Set(list.map((inv) => inv.dc_id).filter(Boolean)), [list]);

  // One reference row per not-yet-invoiced DC, tagged with why it is (or
  // isn't) actually invoiceable right now. `eligibleDcs` (kind=customer,
  // dispatched/delivered, no invoice) is the only subset that's clickable —
  // it's the same set that already fed the "Generate Invoice" dropdown, so
  // that dropdown/action is completely unchanged. Subcontract DCs, drafts
  // and cancelled DCs still "reflect" here (per the ask: all 115 should be
  // visible) but are inert — the backend itself rejects generating a Tax
  // Invoice against any of them.
  const eligibleDcIds = useMemo(() => new Set(eligibleDcs.map((d) => d.id)), [eligibleDcs]);
  const dcReferenceRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dcs
      .filter((d) => !invoicedDcIds.has(d.id))
      .filter((d) => {
        if (!q) return true;
        return (
          (d.code || "").toLowerCase().includes(q) ||
          (d.customer_so_code || d.so_code || "").toLowerCase().includes(q) ||
          (custMap[d.customer_id]?.name || "").toLowerCase().includes(q) ||
          (d.vendor_name || "").toLowerCase().includes(q)
        );
      })
      .map((d) => {
        let tag = "Not Invoiced", tone = "amber";
        if (d.kind !== "customer") { tag = "Subcontract DC"; tone = "slate"; }
        else if (d.status === "ready" || d.status === "draft") { tag = "Ready — Not Dispatched"; tone = "slate"; }
        else if (d.status === "cancelled") { tag = "Cancelled DC"; tone = "slate"; }
        return {
          _rowKind: "dc_ref", key: `dc-${d.id}`, date: d.dispatch_date || d.created_at, data: d,
          tag, tone, clickable: canEdit && eligibleDcIds.has(d.id),
        };
      });
  }, [dcs, invoicedDcIds, eligibleDcIds, search, custMap, canEdit]);

  // Invoice rows + every other DC's reference row, merged into one
  // chronological list (newest first) for the sidebar. A specific
  // draft/issued/cancelled *invoice* status filter hides DC reference rows
  // (none of them are an invoice with that status); "not_invoiced" shows
  // only DC reference rows.
  // Tally sales vouchers, minus any that are just this ERP's own invoice
  // pushed to Tally and pulled back (same number as an ERP invoice code).
  const tallyRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const erpCodes = new Set(list.map((inv) => (inv.code || "").trim().toUpperCase()));
    return tallyList
      .filter((v) => !erpCodes.has((v.number || "").trim().toUpperCase()))
      .filter((v) => {
        if (!q) return true;
        return (
          (v.number || "").toLowerCase().includes(q) ||
          (v.party || "").toLowerCase().includes(q) ||
          (v.reference || "").toLowerCase().includes(q)
        );
      })
      .map((v) => ({ _rowKind: "tally", key: `tally-${v.guid}`, date: v.date, data: v }));
  }, [tallyList, list, search]);

  const combinedRows = useMemo(() => {
    const showDcRefs = statusFilter === "all" || statusFilter === "not_invoiced";
    const invoiceRows = ["not_invoiced", "tally"].includes(statusFilter) ? [] : visibleList.map((inv) => ({
      _rowKind: "invoice", key: `inv-${inv.id}`, date: inv.invoice_date, data: inv,
    }));
    const dcRows = showDcRefs ? dcReferenceRows : [];
    const fromTally = statusFilter === "all" || statusFilter === "tally" ? tallyRows : [];
    return [...invoiceRows, ...dcRows, ...fromTally].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [visibleList, dcReferenceRows, tallyRows, statusFilter]);

  const openGenerate = (dcId) => {
    setGenerateForm({ ...EMPTY_GENERATE_FORM, dc_id: dcId || "" });
    setGenerateOpen(true);
  };

  const submitGenerate = async () => {
    if (!generateForm.dc_id) return toast.error("Pick a dispatched Delivery Challan");
    setBusy(true);
    try {
      const { dc_id, ...rest } = generateForm;
      const payload = {
        ...rest,
        due_date: rest.due_date ? new Date(rest.due_date).toISOString() : null,
        po_date: rest.po_date ? new Date(rest.po_date).toISOString() : null,
      };
      const { data } = await api.post(`/delivery-challans/${dc_id}/generate-tax-invoice`, payload);
      toast.success(`Tax Invoice ${data.code} generated`);
      setGenerateOpen(false);
      setGenerateForm(EMPTY_GENERATE_FORM);
      await load();
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generate failed");
    } finally { setBusy(false); }
  };

  const openEdit = (inv) => {
    setEditForm({
      lines: (inv.lines || []).map((l) => ({
        item_id: l.item_id, rate: l.rate, discount_pct: l.discount_pct || 0, hsn_code: l.hsn_code || "",
        cgst_pct: l.cgst_pct || 0, sgst_pct: l.sgst_pct || 0, igst_pct: l.igst_pct || 0,
      })),
      due_date: toDateInput(inv.due_date),
      po_reference: inv.po_reference || "",
      po_date: toDateInput(inv.po_date),
      eway_bill_no: inv.eway_bill_no || "",
      vehicle_no: inv.vehicle_no || "",
      transporter: inv.transporter || "",
      destination: inv.destination || "",
      terms_of_delivery: inv.terms_of_delivery || "",
      reverse_charge: !!inv.reverse_charge,
      notes: inv.notes || "",
    });
    setEditOpen(true);
  };

  const setEditLine = (idx, patch) => setEditForm((f) => ({
    ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/tax-invoices/${selected.id}`, {
        ...editForm,
        due_date: editForm.due_date ? new Date(editForm.due_date).toISOString() : null,
        po_date: editForm.po_date ? new Date(editForm.po_date).toISOString() : null,
      });
      toast.success(`${selected.code} updated`);
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const issueInvoice = async () => {
    setBusy(true);
    try {
      await api.post(`/tax-invoices/${selected.id}/issue`);
      toast.success(`${selected.code} issued`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Issue failed");
    } finally { setBusy(false); }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) return toast.error("A cancellation reason is required");
    setBusy(true);
    try {
      await api.post(`/tax-invoices/${selected.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(`${selected.code} cancelled`);
      setCancelOpen(false);
      setCancelReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const generateEinvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tax-invoices/${selected.id}/generate-einvoice`);
      toast.success(`e-Invoice generated — IRN ${data.einvoice_irn?.slice(0, 16)}…`);
      await load();
      setSelected(data);
    } catch (e) {
      // Surface the IRP/GSP's own validation or rejection message — never fail silently.
      toast.error(formatApiError(e.response?.data?.detail) || "e-Invoice generation failed");
    } finally { setBusy(false); }
  };

  const submitCancelEinvoice = async () => {
    if (!einvoiceCancelForm.remarks.trim()) return toast.error("Cancellation remarks are required");
    setBusy(true);
    try {
      const { data } = await api.post(`/tax-invoices/${selected.id}/cancel-einvoice`, einvoiceCancelForm);
      toast.success(`e-Invoice for ${selected.code} cancelled`);
      setEinvoiceCancelOpen(false);
      setEinvoiceCancelForm({ reason_code: "4", remarks: "" });
      await load();
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "e-Invoice cancellation failed");
    } finally { setBusy(false); }
  };

  const removeInvoice = async (inv) => {
    if (!window.confirm(`Delete draft invoice ${inv.code}?`)) return;
    try {
      await api.delete(`/tax-invoices/${inv.id}`);
      if (selected?.id === inv.id) setSelected(null);
      toast.success(`${inv.code} deleted`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/tax-invoices/${selected.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${selected.code.replace(/[\\/:*?"<>|]+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.code}.pdf`);
    } catch { toast.error("PDF download failed"); }
  };

  const downloadTallyXml = async () => {
    try {
      const res = await api.get(`/tax-invoices/${selected.id}/tally-xml`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a"); a.href = url; a.download = `${selected.code.replace(/[\\/:*?"<>|]+/g, "-")}_tally.xml`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.code}_tally.xml`);
    } catch { toast.error("Tally XML export failed"); }
  };

  const pushToTally = async () => {
    setBusy(true);
    const push = (force) => api.post(`/tax-invoices/${selected.id}/tally-push`, null, { params: force ? { force: true } : {} });
    // Already in Tally → the server alters that voucher (never books a second
    // one). Only after an update failed because the voucher was deleted in
    // Tally is a new voucher offered.
    const asNew = (selected.tally_push_error || "").includes("Send as a new voucher") &&
      window.confirm(`Send ${selected.code} to Tally as a NEW voucher?\n\nOnly do this if you deleted its voucher in Tally — otherwise Tally will have it twice.`);
    try {
      const res = await push(asNew);
      toast.success(res.data?.queued ? `${selected.code} queued for the Tally Bridge Agent` : `${selected.code} sent to Tally`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Tally push failed");
    } finally { setBusy(false); }
  };

  // Print just the selected invoice's formatted document (same layout as the
  // PDF) in its own window — plain window.print() printed this whole SPA
  // page (invoice list, sidebar, everything currently on screen), not the
  // one invoice the user has open.
  const printInvoice = async () => {
    try {
      const r = await api.get(`/tax-invoices/${selected.id}/render-html`, { responseType: "text" });
      const w = window.open("", "_blank");
      w.document.write(`<html><head><title>${selected.code}</title></head><body>${r.data}<script>window.onload=()=>setTimeout(()=>window.print(),200)</script></body></html>`);
      w.document.close();
    } catch {
      toast.error("Print failed");
    }
  };

  // The "Export" button — a CSV of every invoice, line-item-level, with every
  // GST/Tally-relevant field, ready for Excel or a Tally/GSTR import.
  const exportCsv = async () => {
    try {
      const res = await api.get("/tax-invoices/export-csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "tax_invoices_export.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Exported tax_invoices_export.csv");
    } catch { toast.error("Export failed"); }
  };

  const XLSX_MEDIA = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  // "Invoice pending DCs" — raise the Tax Invoice for every dispatched
  // customer DC that has none yet; each one is emailed (PDF attached) to the
  // billing team set in Settings → Dispatch & Packing Notifications.
  const generatePending = async () => {
    if (!window.confirm(`Generate Tax Invoices for ${eligibleDcs.length} dispatched DC(s) without one, and email them to the billing team?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tax-invoices/generate-pending");
      const flagged = data.created.filter((c) => c.verification_issues?.length).length;
      if (data.created.length) {
        toast.success(`Generated ${data.created.length} Tax Invoice(s) and emailed billing`, {
          description: flagged ? `${flagged} need a review before issuing — see the invoice's verification note` : undefined,
        });
      }
      if (data.failed.length) {
        toast.error(`${data.failed.length} DC(s) could not be invoiced`, {
          description: data.failed.slice(0, 3).map((f) => `${f.dc_code}: ${f.error}`).join(" | "),
        });
      }
      if (!data.created.length && !data.failed.length) toast.info("Every dispatched DC already has a Tax Invoice");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generate failed");
    } finally { setBusy(false); }
  };

  // "Template" — the blank fillable workbook the Import button expects back:
  // one Delivery Challan Code (which supplies customer/pricing/HSN/GST%) plus
  // the header fields and optional per-item overrides.
  const downloadImportTemplate = async () => {
    try {
      const res = await api.get("/tax-invoices/import-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: XLSX_MEDIA }));
      const a = document.createElement("a"); a.href = url; a.download = "tax_invoice_import_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Downloaded tax_invoice_import_template.xlsx");
    } catch { toast.error("Template download failed"); }
  };

  // "Import" — upload a filled-in template to generate a new draft invoice
  // against the Delivery Challan named on it.
  const importTaxInvoice = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/tax-invoices/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.code} from ${file.name}`);
      if (data.import_warnings?.length) {
        toast.warning(`${data.import_warnings.length} line override(s) ignored`, {
          description: data.import_warnings.slice(0, 3).join(" | "),
        });
      }
      await load();
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  // "Export XLSX" (per invoice) — a print-ready GST Tax Invoice workbook in
  // the standard Tally layout (company/buyer/consignee blocks, Tally & GST
  // fields, HSN-wise items, tax summary, amount-in-words), filled in with
  // this invoice's actual data. Separate from the Template/Import layout.
  const exportInvoiceXlsx = async () => {
    try {
      const res = await api.get(`/tax-invoices/${selected.id}/export-xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: XLSX_MEDIA }));
      const a = document.createElement("a"); a.href = url; a.download = `${selected.code.replace(/[\\/:*?"<>|]+/g, "-")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.code}.xlsx`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="tax-invoices-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Tax Invoices
          </h1>
          <p className="text-sm text-slate-500 mt-1">Raised against a dispatched Delivery Challan — HSN/GST-wise, with every Tally field. Download the Template, fill it in, and Import it back, or Export any invoice as a print-ready GST Tax Invoice workbook.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadImportTemplate} data-testid="tax-invoice-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="tax-invoice-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
          {canEdit && (
            <label
              className="inline-flex items-center h-10 rounded-md border border-slate-200 px-3 text-sm cursor-pointer hover:bg-slate-50"
              data-testid="tax-invoice-import-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" /> Import
              <input
                type="file"
                accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={importTaxInvoice}
                data-testid="tax-invoice-import-input"
              />
            </label>
          )}
          {canEdit && (
            <Button variant="outline" onClick={generatePending} disabled={busy || !eligibleDcs.length}
              title="Raise and email the Tax Invoice for every dispatched DC that has none yet" data-testid="tax-invoice-generate-pending-btn">
              <Files className="w-4 h-4 mr-1.5" /> Invoice Pending DCs ({eligibleDcs.length})
            </Button>
          )}
          {canEdit && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => openGenerate()} data-testid="new-tax-invoice-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Generate Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Generate sheet */}
      <Sheet open={generateOpen} onOpenChange={(v) => { setGenerateOpen(v); if (!v) setGenerateForm(EMPTY_GENERATE_FORM); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>Generate Tax Invoice</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4">
            <div>
              <Label>Delivery Challan *</Label>
              <Select value={generateForm.dc_id} onValueChange={(v) => setGenerateForm({ ...generateForm, dc_id: v })}>
                <SelectTrigger data-testid="tax-invoice-dc-select"><SelectValue placeholder="Pick a dispatched DC" /></SelectTrigger>
                <SelectContent>
                  {eligibleDcs.length === 0 && <SelectItem value="__none__" disabled>No un-invoiced dispatched DCs</SelectItem>}
                  {eligibleDcs.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.code} — {custMap[d.customer_id]?.name || "—"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">Rate, HSN and GST% are pulled from this DC's Sales Order automatically.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due Date</Label><Input type="date" value={generateForm.due_date} onChange={(e) => setGenerateForm({ ...generateForm, due_date: e.target.value })} /></div>
              <div><Label>E-Way Bill No</Label><Input value={generateForm.eway_bill_no} onChange={(e) => setGenerateForm({ ...generateForm, eway_bill_no: e.target.value })} data-testid="tax-invoice-ewb-input" /></div>
              <div><Label>PO Reference</Label><Input value={generateForm.po_reference} onChange={(e) => setGenerateForm({ ...generateForm, po_reference: e.target.value })} /></div>
              <div><Label>PO Date</Label><Input type="date" value={generateForm.po_date} onChange={(e) => setGenerateForm({ ...generateForm, po_date: e.target.value })} /></div>
              <div><Label>Destination</Label><Input value={generateForm.destination} onChange={(e) => setGenerateForm({ ...generateForm, destination: e.target.value })} /></div>
              <div><Label>Terms of Delivery</Label><Input value={generateForm.terms_of_delivery} onChange={(e) => setGenerateForm({ ...generateForm, terms_of_delivery: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={generateForm.reverse_charge} onCheckedChange={(v) => setGenerateForm({ ...generateForm, reverse_charge: !!v })} data-testid="tax-invoice-reverse-charge" />
              <Label className="font-normal">Reverse Charge Applicable</Label>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={generateForm.notes} onChange={(e) => setGenerateForm({ ...generateForm, notes: e.target.value })} /></div>
          </div>
          <SheetFooter className="mt-6">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitGenerate} disabled={busy} data-testid="tax-invoice-generate-submit">
              <FileCheck2 className="w-4 h-4 mr-1.5" /> Generate Invoice
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit sheet — draft only */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader><SheetTitle>Edit {selected?.code}</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-5">
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">Line Items</div>
              <div className="space-y-3">
                {editForm.lines.map((ln, idx) => {
                  const it = itemMap[ln.item_id];
                  return (
                    <div key={idx} className="border border-slate-200 rounded-md p-3 bg-white">
                      <div className="text-sm font-medium text-slate-800 mb-2">{it?.code} — {it?.name}</div>
                      <div className="grid grid-cols-5 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase">HSN</Label>
                          <Input value={ln.hsn_code} onChange={(e) => setEditLine(idx, { hsn_code: e.target.value })} data-testid={`tax-invoice-edit-hsn-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase">Rate ({currencySymbol(docCurrency(selected))})</Label>
                          <Input type="number" value={ln.rate} onChange={(e) => setEditLine(idx, { rate: parseFloat(e.target.value || 0) })} data-testid={`tax-invoice-edit-rate-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase">Disc %</Label>
                          <Input type="number" value={ln.discount_pct} onChange={(e) => setEditLine(idx, { discount_pct: parseFloat(e.target.value || 0) })} data-testid={`tax-invoice-edit-disc-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase">CGST %</Label>
                          <Input type="number" value={ln.cgst_pct} onChange={(e) => setEditLine(idx, { cgst_pct: parseFloat(e.target.value || 0) })} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase">SGST %</Label>
                          <Input type="number" value={ln.sgst_pct} onChange={(e) => setEditLine(idx, { sgst_pct: parseFloat(e.target.value || 0) })} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due Date</Label><Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} /></div>
              <div><Label>E-Way Bill No</Label><Input value={editForm.eway_bill_no} onChange={(e) => setEditForm({ ...editForm, eway_bill_no: e.target.value })} data-testid="tax-invoice-edit-ewb" /></div>
              <div><Label>PO Reference</Label><Input value={editForm.po_reference} onChange={(e) => setEditForm({ ...editForm, po_reference: e.target.value })} /></div>
              <div><Label>PO Date</Label><Input type="date" value={editForm.po_date} onChange={(e) => setEditForm({ ...editForm, po_date: e.target.value })} /></div>
              <div><Label>Vehicle No</Label><Input value={editForm.vehicle_no} onChange={(e) => setEditForm({ ...editForm, vehicle_no: e.target.value })} /></div>
              <div><Label>Transporter</Label><Input value={editForm.transporter} onChange={(e) => setEditForm({ ...editForm, transporter: e.target.value })} /></div>
              <div><Label>Destination</Label><Input value={editForm.destination} onChange={(e) => setEditForm({ ...editForm, destination: e.target.value })} /></div>
              <div><Label>Terms of Delivery</Label><Input value={editForm.terms_of_delivery} onChange={(e) => setEditForm({ ...editForm, terms_of_delivery: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={editForm.reverse_charge} onCheckedChange={(v) => setEditForm({ ...editForm, reverse_charge: !!v })} />
              <Label className="font-normal">Reverse Charge Applicable</Label>
            </div>
            <div><Label>Notes</Label><Textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
          </div>
          <SheetFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="tax-invoice-edit-save">Save Changes</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cancel {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason *</Label>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Why is this invoice being cancelled?" data-testid="tax-invoice-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={submitCancel} disabled={busy} data-testid="tax-invoice-cancel-confirm">
              <XCircle className="w-4 h-4 mr-1.5" />Confirm Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel e-Invoice dialog — the IRN itself, separate from cancelling the
          Tax Invoice document above. Only valid within 24h of generation. */}
      <Dialog open={einvoiceCancelOpen} onOpenChange={setEinvoiceCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cancel e-Invoice {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              IRN: <span className="mono">{selected?.einvoice_irn}</span>
            </div>
            <div>
              <Label>Reason</Label>
              <Select
                value={einvoiceCancelForm.reason_code}
                onValueChange={(v) => setEinvoiceCancelForm({ ...einvoiceCancelForm, reason_code: v })}
              >
                <SelectTrigger data-testid="einvoice-cancel-reason-code"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Duplicate</SelectItem>
                  <SelectItem value="2">Data Entry Mistake</SelectItem>
                  <SelectItem value="3">Order Cancelled</SelectItem>
                  <SelectItem value="4">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks *</Label>
              <Textarea
                rows={3}
                value={einvoiceCancelForm.remarks}
                onChange={(e) => setEinvoiceCancelForm({ ...einvoiceCancelForm, remarks: e.target.value })}
                placeholder="Why is this e-Invoice being cancelled?"
                data-testid="einvoice-cancel-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEinvoiceCancelOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={submitCancelEinvoice} disabled={busy} data-testid="einvoice-cancel-confirm">
              <XCircle className="w-4 h-4 mr-1.5" />Confirm Cancel e-Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master / detail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>Invoices</span>
            <span className="text-xs text-slate-500 mono">{list.length} invoiced · {dcs.length} DC{dcs.length === 1 ? "" : "s"} total{tallyRows.length ? ` · ${tallyRows.length} from Tally` : ""}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-100 space-y-2">
            <Input
              placeholder="Search by invoice/DC/SO code, customer, PO ref..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="tax-invoice-search-input"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs" data-testid="tax-invoice-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="not_invoiced">Not Invoiced (DC)</SelectItem>
                <SelectItem value="tally">From Tally</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {combinedRows.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-tax-invoices">No tax invoices or dispatched DCs match.</div>}
            {combinedRows.map((row) => {
              if (row._rowKind === "invoice") {
                const inv = row.data;
                return (
                  <button key={row.key} onClick={() => { setSelectedTally(null); setSelected(inv); }} data-testid={`tax-invoice-row-${inv.code}`}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${!selectedTally && selected?.id === inv.id ? "bg-blue-50/60" : ""} ${inv.status === "cancelled" ? "opacity-60" : ""}`}>
                    <div className="flex items-center justify-between">
                      <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{invoiceNo(inv)}</div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[inv.status] || "bg-slate-100"}`}>{inv.status}</span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-slate-800">{custMap[inv.customer_id]?.name || inv.customer_name || "—"}</div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      DC <span className="mono">{inv.dc_code}</span> · {formatMoney(Number(inv.grand_total || 0), docCurrency(inv))} · {fmtDate(inv.invoice_date)}
                    </div>
                  </button>
                );
              }
              if (row._rowKind === "tally") {
                const v = row.data;
                return (
                  <button key={row.key} onClick={() => { setSelected(null); setSelectedTally(v); }} data-testid={`tax-invoice-tally-row-${v.number}`}
                    className={`w-full text-left px-4 py-3 border-l-2 border-violet-300 hover:bg-violet-50/50 transition ${selectedTally?.guid === v.guid ? "bg-violet-50/70" : ""} ${v.cancelled ? "opacity-60" : ""}`}>
                    <div className="flex items-center justify-between">
                      <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{v.number || "—"}</div>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{v.cancelled ? "Tally · Cancelled" : "From Tally"}</span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-slate-800">{v.party || "—"}</div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {formatMoney(Number(v.amount || 0), "INR")} · {fmtDate(v.date)}{v.reference ? ` · Ref ${v.reference}` : ""}
                    </div>
                  </button>
                );
              }
              // dc_ref — every Delivery Challan not already covered by its
              // own invoice row: an invoiceable customer DC (amber, clickable
              // for canEdit users, opens Generate pre-filled), or a
              // subcontract/draft/cancelled DC shown for reference only
              // (backend rejects generating a Tax Invoice against any of
              // those, so they're inert here too).
              const d = row.data;
              const toneClasses = row.tone === "amber"
                ? "border-amber-300"
                : "border-slate-200 opacity-70";
              const badgeClasses = row.tone === "amber"
                ? "bg-amber-50 text-amber-700"
                : "bg-slate-100 text-slate-500";
              const Row = row.clickable ? "button" : "div";
              const statusLabel = d.kind !== "customer" ? "Subcontract"
                : d.status === "delivered" ? "Delivered"
                : d.status === "dispatched" ? "Dispatched"
                : d.status === "cancelled" ? "Cancelled" : "Draft";
              return (
                <Row
                  key={row.key}
                  {...(row.clickable ? { onClick: () => openGenerate(d.id) } : {})}
                  data-testid={`tax-invoice-dc-ref-${d.code}`}
                  className={`w-full text-left px-4 py-3 border-l-2 ${toneClasses} ${row.clickable ? "hover:bg-amber-50/60 transition cursor-pointer" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{d.code}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${badgeClasses}`}>{row.tag}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-800">{custMap[d.customer_id]?.name || d.vendor_name || "—"}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {statusLabel} {fmtDate(d.dispatch_date || d.created_at)}
                    {row.clickable ? " · Click to generate invoice" : ""}
                  </div>
                </Row>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="tax-invoice-detail">
          {selectedTally ? (
            <TallyInvoiceDetail v={selectedTally} />
          ) : !selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <FileCheck2 className="w-10 h-10 text-slate-300" /> Select a tax invoice to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{invoiceNo(selected)}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {custMap[selected.customer_id]?.name || selected.customer_name || "—"}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1">
                    Invoice Date: <span className="mono">{fmtDate(selected.invoice_date)}</span> · Due: <span className="mono">{fmtDate(selected.due_date)}</span>
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[selected.status] || "bg-slate-100"}`}>{selected.status}</span>
              </div>

              {selected.status === "cancelled" && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-md" data-testid="tax-invoice-cancelled-banner">
                  <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Cancelled{selected.cancelled_by ? ` by ${selected.cancelled_by}` : ""}{selected.cancelled_at ? ` on ${new Date(selected.cancelled_at).toLocaleString()}` : ""}</div>
                    {selected.cancel_reason && <div>Reason: {selected.cancel_reason}</div>}
                  </div>
                </div>
              )}

              {/* Document trail */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm bg-slate-50 border border-slate-100 rounded-md p-3" data-testid="tax-invoice-doc-refs">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Invoice No.</div>
                  <div className="font-semibold mono text-slate-900" data-testid="tax-invoice-number">{invoiceNo(selected)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">DC No.</div>
                  <div className="font-semibold mono text-blue-700">{selected.dc_code || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">SO No.</div>
                  <div className="font-semibold mono text-blue-700">{selected.customer_so_code || selected.so_code || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">PO No.</div>
                  <div className="font-semibold mono text-slate-900">{selected.po_reference || "—"}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap" data-testid="tax-invoice-actions">
                {canEdit && selected.status === "draft" && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(selected)} data-testid="tax-invoice-edit-btn">
                    <FileEdit className="w-4 h-4 mr-1.5" />Edit
                  </Button>
                )}
                {canEdit && selected.status === "draft" && (
                  <Button size="sm" onClick={issueInvoice} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tax-invoice-issue-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" />Issue
                  </Button>
                )}
                {!selected.tally_number_pending && (<>
                  <Button variant="outline" size="sm" onClick={printInvoice} data-testid="tax-invoice-print-btn"><Printer className="w-4 h-4 mr-1.5" />Print</Button>
                  <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="tax-invoice-pdf-btn"><Download className="w-4 h-4 mr-1.5" />PDF</Button>
                  <Button variant="outline" size="sm" onClick={exportInvoiceXlsx} data-testid="tax-invoice-export-xlsx-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Export XLSX</Button>
                </>)}
                <Button variant="outline" size="sm" onClick={downloadTallyXml} data-testid="tax-invoice-tally-xml-btn"><Receipt className="w-4 h-4 mr-1.5" />Tally XML</Button>
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={pushToTally} disabled={busy} data-testid="tax-invoice-tally-push-btn">
                    <UploadCloud className="w-4 h-4 mr-1.5" />Push to Tally
                  </Button>
                )}
                {canEinvoice && selected.status === "issued" && !selected.tally_number_pending &&
                  ["not_generated", "failed", undefined].includes(selected.einvoice_status) && (
                  <Button
                    variant="outline" size="sm" onClick={generateEinvoice} disabled={busy}
                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    data-testid="tax-invoice-generate-einvoice-btn"
                  >
                    <QrCode className="w-4 h-4 mr-1.5" />
                    {selected.einvoice_status === "failed" ? "Retry e-Invoice" : "Generate e-Invoice"}
                  </Button>
                )}
                {canEinvoice && selected.einvoice_status === "generated" && (
                  <Button
                    variant="outline" size="sm" onClick={() => setEinvoiceCancelOpen(true)}
                    className="text-red-600 hover:text-red-700" data-testid="tax-invoice-cancel-einvoice-btn"
                  >
                    <ShieldAlert className="w-4 h-4 mr-1.5" />Cancel e-Invoice
                  </Button>
                )}
                {canCancel && selected.status !== "cancelled" && (
                  <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="text-red-600 hover:text-red-700" data-testid="tax-invoice-cancel-btn">
                    <XCircle className="w-4 h-4 mr-1.5" />Cancel
                  </Button>
                )}
                {canDelete && selected.status === "draft" && (
                  <Button variant="ghost" size="icon" onClick={() => removeInvoice(selected)} className="ml-auto" data-testid="tax-invoice-delete-btn">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>

              {selected.tally_number_pending && (
                <div className={`text-xs px-3 py-2 rounded-md ${selected.tally_number_error ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`} data-testid="tax-invoice-tally-number-pending">
                  The invoice number comes from Tally. {selected.tally_push_status === "pushed" || selected.tally_push_status === "queued"
                    ? "Waiting for Tally's voucher number — it appears here once Tally has numbered the Sales voucher."
                    : "Issue the invoice (or Push to Tally) and Tally's voucher number is pulled in automatically."}
                  {selected.tally_number_error ? ` — ${selected.tally_number_error}` : ""}
                </div>
              )}

              {selected.tally_push_status && (
                <div className={`text-xs px-3 py-2 rounded-md ${selected.tally_push_status === "pushed" ? "bg-emerald-50 text-emerald-700" : selected.tally_push_status === "queued" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`} data-testid="tax-invoice-tally-status">
                  Tally push {selected.tally_push_status}{selected.tally_pushed_at ? ` · ${new Date(selected.tally_pushed_at).toLocaleString()}` : ""}
                  {selected.tally_push_error ? ` — ${selected.tally_push_error}` : ""}
                </div>
              )}

              {selected.tally_payment_status && (
                <div className={`text-xs px-3 py-2 rounded-md ${selected.tally_payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : selected.tally_payment_status === "partial" ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-600"}`} data-testid="tax-invoice-tally-payment">
                  Payment (from Tally): {selected.tally_payment_status} · received ₹{Number(selected.tally_paid_amount || 0).toLocaleString("en-IN")} · balance ₹{Number(selected.tally_balance_due || 0).toLocaleString("en-IN")}
                  {(selected.tally_receipts || []).length > 0 && ` — ${selected.tally_receipts.map((r) => `${r.number} on ${r.date}`).join(", ")}`}
                </div>
              )}

              {selected.einvoice_status && selected.einvoice_status !== "not_generated" && (
                <div
                  className={`text-xs px-3 py-2 rounded-md space-y-0.5 ${
                    selected.einvoice_status === "generated" ? "bg-emerald-50 text-emerald-700"
                    : selected.einvoice_status === "cancelled" ? "bg-slate-100 text-slate-600"
                    : "bg-red-50 text-red-700"
                  }`}
                  data-testid="tax-invoice-einvoice-status"
                >
                  <div className="font-semibold uppercase tracking-wide text-[10px]">
                    e-Invoice {selected.einvoice_status}
                  </div>
                  {selected.einvoice_status === "failed" && (
                    <div>{selected.einvoice_error || "Unknown error"}</div>
                  )}
                  {selected.einvoice_irn && (
                    <div>IRN: <span className="mono break-all">{selected.einvoice_irn}</span></div>
                  )}
                  {selected.einvoice_ack_no && (
                    <div>
                      Ack No: <span className="mono">{selected.einvoice_ack_no}</span>
                      {selected.einvoice_ack_date && <> · Ack Date: {new Date(selected.einvoice_ack_date).toLocaleString()}</>}
                    </div>
                  )}
                  {selected.einvoice_status === "cancelled" && selected.einvoice_cancel_reason && (
                    <div>Reason: {selected.einvoice_cancel_reason}</div>
                  )}
                </div>
              )}

              {/* Tally / GST-compliance fields */}
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Tally &amp; GST Fields</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm bg-white border border-slate-200 rounded-md p-3" data-testid="tax-invoice-tally-fields">
                  <div><div className="text-[10px] uppercase text-slate-500">Place of Supply</div><div className="font-medium">{selected.place_of_supply || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">GST Treatment</div><div className="font-medium">{selected.gst_type === "intra" ? "CGST + SGST" : selected.gst_type === "inter" ? "IGST" : "Undetermined"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Reverse Charge</div><div className="font-medium">{selected.reverse_charge ? "Yes" : "No"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Customer GSTIN</div><div className="font-medium mono">{selected.customer_gstin || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">E-Way Bill No</div><div className="font-medium mono">{selected.eway_bill_no || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Vehicle No</div><div className="font-medium mono">{selected.vehicle_no || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Dispatched Through</div><div className="font-medium">{selected.transporter || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Destination</div><div className="font-medium">{selected.destination || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Terms of Delivery</div><div className="font-medium">{selected.terms_of_delivery || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Buyer's Order No</div><div className="font-medium">{selected.po_reference || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Buyer's Order Date</div><div className="font-medium">{fmtDate(selected.po_date)}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Dispatch Doc No</div><div className="font-medium mono">{selected.dispatch_doc_no || "—"}</div></div>
                </div>
              </div>

              {selected.verification_issues?.length > 0 && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="tax-invoice-verification">
                  <div className="font-semibold mb-1">Review before issuing — verification found:</div>
                  <ul className="list-disc pl-4">{selected.verification_issues.map((i) => <li key={i}>{i}</li>)}</ul>
                </div>
              )}
              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic" data-testid="tax-invoice-notes">{selected.notes}</div>
              )}

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Line Items</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                        <th className="py-2 px-3">Part No</th>
                        <th className="py-2 px-3">Description</th>
                        <th className="py-2 px-3">HSN</th>
                        <th className="py-2 px-3">Qty</th>
                        <th className="py-2 px-3">Rate</th>
                        <th className="py-2 px-3">Disc</th>
                        <th className="py-2 px-3">Taxable</th>
                        <th className="py-2 px-3">GST</th>
                        <th className="py-2 px-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.lines || []).map((l, idx) => (
                        <tr key={idx} className="border-t border-slate-100" data-testid={`tax-invoice-line-${idx}`}>
                          <td className="py-2 px-3 mono text-xs text-slate-500">{itemMap[l.item_id]?.code || l.part_code}</td>
                          <td className="py-2 px-3 text-slate-700">{itemMap[l.item_id]?.name || l.part_name}</td>
                          <td className="py-2 px-3 mono">{l.hsn_code || "—"}</td>
                          <td className="py-2 px-3 mono">{l.qty} {l.uom}</td>
                          <td className="py-2 px-3 mono">{formatMoney(Number(l.rate), docCurrency(selected))}</td>
                          <td className="py-2 px-3 mono">{l.discount_pct || 0}%</td>
                          <td className="py-2 px-3 mono">{formatMoney(Number(l.taxable_value || 0), docCurrency(selected))}</td>
                          <td className="py-2 px-3 mono">{(l.cgst_pct || 0) + (l.sgst_pct || 0) + (l.igst_pct || 0)}%</td>
                          <td className="py-2 px-3 mono font-semibold">{formatMoney(Number(l.line_total || 0), docCurrency(selected))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="bg-blue-50/50 border border-blue-100 rounded-md p-3 w-80 text-xs">
                  <div className="flex justify-between py-0.5"><span>Taxable Value</span><span className="mono">{formatMoney(Number(selected.taxable_total || 0), docCurrency(selected), { decimals: 2 })}</span></div>
                  <div className="flex justify-between py-0.5"><span>CGST</span><span className="mono">{formatMoney(Number(selected.cgst_total || 0), docCurrency(selected), { decimals: 2 })}</span></div>
                  <div className="flex justify-between py-0.5"><span>SGST</span><span className="mono">{formatMoney(Number(selected.sgst_total || 0), docCurrency(selected), { decimals: 2 })}</span></div>
                  <div className="flex justify-between py-0.5"><span>IGST</span><span className="mono">{formatMoney(Number(selected.igst_total || 0), docCurrency(selected), { decimals: 2 })}</span></div>
                  <div className="flex justify-between py-0.5"><span>Round Off</span><span className="mono">{formatMoney(Number(selected.round_off || 0), docCurrency(selected), { decimals: 2 })}</span></div>
                  <div className="flex justify-between py-1 mt-1 border-t border-blue-200 font-bold"><span>Grand Total</span><span className="mono" data-testid="tax-invoice-detail-grand">{formatMoney(Number(selected.grand_total || 0), docCurrency(selected), { withCode: true })}</span></div>
                </div>
              </div>

              <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2" data-testid="tax-invoice-detail-audit">
                Created by <span className="mono">{selected.created_by || "—"}</span> on {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
                {selected.issued_by && <> · Issued by <span className="mono">{selected.issued_by}</span> on {selected.issued_at ? new Date(selected.issued_at).toLocaleString() : "—"}</>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Read-only view of a sales voucher pulled from Tally. It lives in Tally, so
// there is nothing to edit, issue or e-invoice here.
function TallyInvoiceDetail({ v }) {
  const lines = v.inventory || [];
  return (
    <div className="space-y-5" data-testid="tax-invoice-tally-detail">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{v.number || "—"}</div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>{v.party || "—"}</h2>
          <div className="text-xs text-slate-500 mt-1">
            Date: <span className="mono">{fmtDate(v.date)}</span> · Voucher type: {v.voucher_type || v.sync_type || "—"}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{v.cancelled ? "Cancelled in Tally" : "From Tally"}</span>
      </div>
      <div className="text-xs bg-violet-50 border border-violet-200 text-violet-800 px-3 py-2 rounded-md">
        Pulled from Tally by the Tally Bridge Agent — read-only here. Make changes in Tally; they show up after the next sync.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm bg-slate-50 border border-slate-100 rounded-md p-3">
        <div><div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Voucher No.</div><div className="font-semibold mono text-slate-900">{v.number || "—"}</div></div>
        <div><div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Reference</div><div className="font-semibold mono text-slate-900 break-words">{v.reference || "—"}</div></div>
        <div><div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Date</div><div className="font-semibold mono text-slate-900">{fmtDate(v.date)}</div></div>
        <div><div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Amount</div><div className="font-semibold mono text-slate-900">{formatMoney(Number(v.amount || 0), "INR")}</div></div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">No item lines on this voucher.</td></tr>}
            {lines.map((l, i) => (
              <tr key={i}>
                <td className="px-3 py-2">{l.item}{l.item_code && <span className="ml-1 mono text-xs text-slate-500">({l.item_code})</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{l.qty} {l.uom}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatMoney(Number(l.rate || 0), "INR")}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatMoney(Number(l.amount || 0), "INR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {v.narration && <div className="text-sm text-slate-600"><span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mr-2">Narration</span>{v.narration}</div>}
    </div>
  );
}
